import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  GroupBuyReconciliationNumberSchema,
  isGroupBuyReconciliationNumber,
} from "./group-buy-reconciliation-number.js";
import { isOrderNumber, OrderNumberSchema } from "./order-number.js";
import { isPurchaseOrderNumber, PurchaseOrderNumberSchema } from "./purchase-order-number.js";

const BUSINESS_OPERATION_ID_PATTERN = "^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$";

export const BusinessOperationIdSchema = Type.String({
  minLength: 8,
  maxLength: 128,
  pattern: BUSINESS_OPERATION_ID_PATTERN,
});
export type BusinessOperationId = Static<typeof BusinessOperationIdSchema>;

export const BusinessNumberReferenceSchema = Type.Union([
  Type.Object(
    {
      kind: Type.Literal("ORDER"),
      value: OrderNumberSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("PURCHASE_ORDER"),
      value: PurchaseOrderNumberSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("GROUP_BUY_RECONCILIATION"),
      value: GroupBuyReconciliationNumberSchema,
    },
    { additionalProperties: false },
  ),
]);
export type BusinessNumberReference = Static<typeof BusinessNumberReferenceSchema>;

/**
 * Versioned sidecar for new write paths. It intentionally does not change the strict
 * BusinessEvent 1.0.0 envelope, so historical Inbox/Outbox JSON remains replayable.
 */
export const BusinessOperationTraceV1Schema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    commandId: BusinessOperationIdSchema,
    correlationId: BusinessOperationIdSchema,
    causationId: Type.Union([BusinessOperationIdSchema, Type.Null()]),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
    businessNumber: Type.Union([BusinessNumberReferenceSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type BusinessOperationTraceV1 = Static<typeof BusinessOperationTraceV1Schema>;

export const BusinessOperationTraceSidecarV1Schema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    eventId: Type.String({ minLength: 8 }),
    trace: BusinessOperationTraceV1Schema,
  },
  { additionalProperties: false },
);
export type BusinessOperationTraceSidecarV1 = Static<typeof BusinessOperationTraceSidecarV1Schema>;

export function isBusinessOperationTraceV1(value: unknown): value is BusinessOperationTraceV1 {
  if (!Value.Check(BusinessOperationTraceV1Schema, value)) return false;
  const trace = value as BusinessOperationTraceV1;
  if (trace.businessNumber === null) return true;
  switch (trace.businessNumber.kind) {
    case "ORDER":
      return isOrderNumber(trace.businessNumber.value);
    case "PURCHASE_ORDER":
      return isPurchaseOrderNumber(trace.businessNumber.value);
    case "GROUP_BUY_RECONCILIATION":
      return isGroupBuyReconciliationNumber(trace.businessNumber.value);
  }
}

export function isBusinessOperationTraceSidecarV1(
  value: unknown,
): value is BusinessOperationTraceSidecarV1 {
  return (
    Value.Check(BusinessOperationTraceSidecarV1Schema, value) &&
    isBusinessOperationTraceV1((value as BusinessOperationTraceSidecarV1).trace)
  );
}

/**
 * Binds sidecars to the immutable transport events without adding fields to the
 * strict BusinessEvent 1.0.0 envelope. Unknown, duplicate or mismatched bindings
 * fail closed before a consumer records a different causal fact.
 */
export function indexBusinessOperationTraceSidecarsV1(
  events: ReadonlyArray<{ eventId: string; idempotencyKey: string }>,
  sidecars: readonly BusinessOperationTraceSidecarV1[],
): ReadonlyMap<string, BusinessOperationTraceV1> {
  const eventIdempotencyKeys = new Map<string, string | null>();
  for (const event of events) {
    const existing = eventIdempotencyKeys.get(event.eventId);
    if (existing === undefined) {
      eventIdempotencyKeys.set(event.eventId, event.idempotencyKey);
    } else if (existing !== event.idempotencyKey) {
      eventIdempotencyKeys.set(event.eventId, null);
    }
  }

  const indexed = new Map<string, BusinessOperationTraceV1>();
  for (const sidecar of sidecars) {
    if (!isBusinessOperationTraceSidecarV1(sidecar)) {
      throw new TypeError("business operation trace sidecar is invalid");
    }
    const eventIdempotencyKey = eventIdempotencyKeys.get(sidecar.eventId);
    if (eventIdempotencyKey === undefined) {
      throw new TypeError("business operation trace sidecar references an unknown event");
    }
    if (eventIdempotencyKey === null) {
      throw new TypeError("business operation trace sidecar event identity is ambiguous");
    }
    if (indexed.has(sidecar.eventId)) {
      throw new TypeError("business operation trace sidecar is duplicated");
    }
    if (sidecar.trace.idempotencyKey !== eventIdempotencyKey) {
      throw new TypeError("business operation trace sidecar idempotency does not match its event");
    }
    indexed.set(sidecar.eventId, sidecar.trace);
  }
  return indexed;
}

export function createBusinessOperationTraceV1(input: {
  commandId: string;
  correlationId: string;
  causationId: string | null;
  idempotencyKey: string;
  businessNumber?: BusinessNumberReference | null;
}): BusinessOperationTraceV1 {
  const trace = {
    contractVersion: "1.0.0" as const,
    commandId: input.commandId,
    correlationId: input.correlationId,
    causationId: input.causationId,
    idempotencyKey: input.idempotencyKey,
    businessNumber: input.businessNumber ?? null,
  };
  if (!isBusinessOperationTraceV1(trace)) {
    throw new TypeError("business operation trace is invalid");
  }
  return trace;
}

function sameBusinessNumberReference(
  left: BusinessNumberReference | null,
  right: BusinessNumberReference | null,
): boolean {
  return left === null
    ? right === null
    : right !== null && left.kind === right.kind && left.value === right.value;
}

/**
 * Creates the next command/effect in one causal chain. Correlation and the public
 * business number are inherited from the parent and cannot silently change.
 */
export function continueBusinessOperationTraceV1(
  parent: BusinessOperationTraceV1,
  input: {
    commandId: string;
    idempotencyKey: string;
    businessNumber?: BusinessNumberReference | null;
  },
): BusinessOperationTraceV1 {
  if (!isBusinessOperationTraceV1(parent)) {
    throw new TypeError("parent business operation trace is invalid");
  }
  if (input.commandId === parent.commandId) {
    throw new TypeError("child business operation command must differ from its parent");
  }
  const businessNumber =
    input.businessNumber === undefined ? parent.businessNumber : input.businessNumber;
  if (!sameBusinessNumberReference(parent.businessNumber, businessNumber)) {
    throw new TypeError("business operation number cannot change inside one causal chain");
  }
  return createBusinessOperationTraceV1({
    commandId: input.commandId,
    correlationId: parent.correlationId,
    causationId: parent.commandId,
    idempotencyKey: input.idempotencyKey,
    businessNumber,
  });
}

/**
 * Compatibility adapter for an existing root command. During migration its stable
 * idempotency key is also the command and correlation identity; child work must use
 * createBusinessOperationTraceV1 with an explicit causationId.
 */
export function upgradeLegacyRootBusinessOperation(input: {
  idempotencyKey: string;
  businessNumber?: BusinessNumberReference | null;
}): BusinessOperationTraceV1 {
  return createBusinessOperationTraceV1({
    commandId: input.idempotencyKey,
    correlationId: input.idempotencyKey,
    causationId: null,
    idempotencyKey: input.idempotencyKey,
    ...(input.businessNumber === undefined ? {} : { businessNumber: input.businessNumber }),
  });
}
